import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
    Box, Typography, Paper, Grid, IconButton, Tooltip, Stack,
    Popover, Checkbox, Chip, Button, LinearProgress,
    Avatar, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import {
    Refresh, Psychology, CheckCircle, AccessTime, TrendingUp, TrendingDown,
    Star, EmojiEvents, BusinessCenter, FolderOpen, PhoneInTalk,
    PhoneMissed, PhoneEnabled, Search,
    SentimentSatisfied, SentimentNeutral, SentimentDissatisfied,
    LocalFireDepartment, Insights, ArrowUpward, ArrowDownward, FilterList, Close, CalendarToday,
    Person,
} from "@mui/icons-material";
import { alpha, styled } from "@mui/material/styles";
import { Doughnut, Bar } from "react-chartjs-2";
import {
    Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend,
    CategoryScale, LinearScale, BarElement, Title,
} from "chart.js";
import { supabase } from "../../supabaseClient";

ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

/* ── Design tokens (matches CallLogsMain.jsx) ──────────────────────────── */
const C = {
    indigo: "#4338ca", indigo2: "#6366f1", indigoSoft: "#eef2ff", indigoBd: "#c7d2fe",
    green: "#16a34a", greenSoft: "#f0fdf4", greenBd: "#bbf7d0",
    red: "#dc2626", redSoft: "#fef2f2", redBd: "#fecaca",
    amber: "#d97706", amberSoft: "#fffbeb", amberBd: "#fde68a",
    sky: "#0284c7", skySoft: "#f0f9ff", skyBd: "#bae6fd",
    page: "#f8fafc", white: "#ffffff",
    border: "#e2e8f0", border2: "#f1f5f9",
    txt1: "#0f172a", txt2: "#334155", txt3: "#64748b", txt4: "#94a3b8",
    sans: "'Geist', system-ui, sans-serif",
    mono: "'Geist Mono', monospace",
};

const LEAD_STATUS_MAP = {
    "0": { label: "New" },
    "1": { label: "Claimed" },
    "2": { label: "In Progress" },
    "3": { label: "Site Visit" },
    "4": { label: "Negotiation" },
    "5": { label: "Booked" },
    "6": { label: "Lost" },
    "7": { label: "On Hold" },
};

const KEYWORDS = [
    {
        key: "interested", label: "Interested", icon: "✅",
        color: C.green, soft: C.greenSoft, bd: C.greenBd,
        patterns: [/interested/i, /इंटरेस्टेड/i, /इच्छुक/i, /keen on/i, /want to book/i, /book karna/i, /ready to (buy|book)/i],
    },
    {
        key: "call_later", label: "Call Later", icon: "🕒",
        color: C.amber, soft: C.amberSoft, bd: C.amberBd,
        patterns: [/call (me )?later/i, /call back/i, /callback/i, /baad me/i, /बाद में/i, /thoda time/i],
    },
    {
        key: "budget", label: "Budget Issue", icon: "💰",
        color: C.red, soft: C.redSoft, bd: C.redBd,
        patterns: [/budget (issue|problem|nahi|not|tight)/i, /too (expensive|costly|high)/i, /महंगा/i, /बजट/i, /can'?t afford/i, /price (is )?(too )?(high|much)/i],
    },
    {
        key: "location", label: "Location Issue", icon: "📍",
        color: C.sky, soft: C.skySoft, bd: C.skyBd,
        patterns: [/location (issue|problem|far|dur|nahi)/i, /too far/i, /लोकेशन/i, /distant/i, /commute/i, /jagah.*door/i],
    },
];
function FilterPanel({ anchorEl, onClose, tab, filters, onApply, smOptions, projectOptions, isLeafUser }) {
    const open = Boolean(anchorEl);

    // Leaf users own only their own records — hide the SM filter for them
    const filterTypes = FILTER_TYPES.filter(ft => !(isLeafUser && ft.key === "sm"));

    const [activeType, setActiveType] = useState(filterTypes[0]?.key);
    const [localFilters, setLocalFilters] = useState(filters);
    const [smSearch, setSmSearch] = useState("");
    const [projSearch, setProjSearch] = useState("");

    useEffect(() => { setLocalFilters(filters); }, [filters, open]);
    useEffect(() => { setActiveType(filterTypes[0]?.key); }, [tab]);

    const toggle = (key, value) =>
        setLocalFilters(prev => {
            const arr = prev[key] || [];
            return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
        });

    const totalSelected =
        (localFilters.sm?.length || 0) +
        (localFilters.project?.length || 0) +
        (localFilters.stage?.length || 0) +
        (localFilters.direction?.length || 0);
    const filteredSms = smOptions.filter(s => s.name?.toLowerCase().includes(smSearch.toLowerCase()));
    const filteredProjects = projectOptions.filter(p => p.projectName?.toLowerCase().includes(projSearch.toLowerCase()));

    const renderOptions = () => {
        if (activeType === "sm") return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                <Box sx={{ px: 1.5, pb: 1 }}>
                    <TextField size="small" placeholder="Search agents…" value={smSearch}
                        onChange={e => setSmSearch(e.target.value)} fullWidth
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 14, color: C.txt4 }} /></InputAdornment>, sx: { fontSize: 12.5, fontFamily: C.sans, borderRadius: 2, background: C.page, "& fieldset": { borderColor: C.border }, height: 32 } }}
                    />
                </Box>
                <Box sx={{ overflowY: "auto", maxHeight: 240 }}>
                    {filteredSms.length === 0
                        ? <Typography sx={{ px: 2, py: 2, fontSize: 12, color: C.txt4, textAlign: "center" }}>No agents found</Typography>
                        : filteredSms.map(sm => {
                            const checked = (localFilters.sm || []).includes(sm.userId);
                            return (
                                <Box key={sm.userId} onClick={() => toggle("sm", sm.userId)}
                                    sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 0.85, cursor: "pointer", background: checked ? C.indigoSoft : "transparent", "&:hover": { background: checked ? C.indigoSoft : C.page }, transition: "background .1s" }}>
                                    <Checkbox size="large" checked={checked} sx={{ p: 0, color: C.border, "&.Mui-checked": { color: C.indigo }, flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: 12.5, color: checked ? C.indigo : C.txt1, fontWeight: checked ? 600 : 400, fontFamily: C.sans, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {sm.name}
                                    </Typography>
                                </Box>
                            );
                        })}
                </Box>
            </Box>
        );

        if (activeType === "project") return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                <Box sx={{ px: 1.5, pb: 1 }}>
                    <TextField size="small" placeholder="Search projects…" value={projSearch}
                        onChange={e => setProjSearch(e.target.value)} fullWidth
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 14, color: C.txt4 }} /></InputAdornment>, sx: { fontSize: 12.5, fontFamily: C.sans, borderRadius: 2, background: C.page, "& fieldset": { borderColor: C.border }, height: 32 } }}
                    />
                </Box>
                <Box sx={{ overflowY: "auto", maxHeight: 240 }}>
                    {filteredProjects.length === 0
                        ? <Typography sx={{ px: 2, py: 2, fontSize: 12, color: C.txt4, textAlign: "center" }}>No projects found</Typography>
                        : filteredProjects.map(proj => {
                            const checked = (localFilters.project || []).includes(proj.projectId);
                            return (
                                <Box key={proj.projectId} onClick={() => toggle("project", proj.projectId)}
                                    sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 0.85, cursor: "pointer", background: checked ? C.indigoSoft : "transparent", "&:hover": { background: checked ? C.indigoSoft : C.page }, transition: "background .1s" }}>
                                    <Checkbox size="large" checked={checked} sx={{ p: 0, color: C.border, "&.Mui-checked": { color: C.indigo }, flexShrink: 0 }} />
                                    <Box sx={{ width: 24, height: 24, borderRadius: 1.5, flexShrink: 0, background: alpha(C.indigo, 0.1), display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <FolderOpen sx={{ fontSize: 12, color: C.indigo }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 12.5, color: checked ? C.indigo : C.txt1, fontWeight: checked ? 600 : 400, fontFamily: C.sans, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {proj.projectName}
                                        </Typography>
                                        {proj.projectShortCode && <Typography sx={{ fontSize: 10, color: C.txt4, fontFamily: C.mono }}>{proj.projectShortCode}</Typography>}
                                    </Box>
                                </Box>
                            );
                        })}
                </Box>
            </Box>
        );

        if (activeType === "stage") {
            return (
                <Box sx={{ overflowY: "auto", maxHeight: 260 }}>
                    {LEAD_STAGE_OPTIONS.map(opt => {
                        const checked = (localFilters.stage || []).includes(opt.value);

                        return (
                            <Box key={opt.value}
                                onClick={() => toggle("stage", opt.value)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.25,
                                    px: 1.5,
                                    py: 0.85,
                                    cursor: "pointer",
                                    background: checked ? C.indigoSoft : "transparent",
                                    "&:hover": { background: checked ? C.indigoSoft : C.page }
                                }}
                            >
                                <Checkbox checked={checked} size="small" />
                                <Typography sx={{
                                    fontSize: 12.5,
                                    color: checked ? C.indigo : C.txt1,
                                    fontWeight: checked ? 600 : 400
                                }}>
                                    {opt.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            );
        }

        if (activeType === "date") {
            const setQuickRange = (type) => {
                const now = new Date();
                let from = null;
                let to = new Date();

                if (type === "today") {
                    from = new Date().setHours(0, 0, 0, 0);
                }

                if (type === "7days") {
                    const d = new Date();
                    d.setDate(d.getDate() - 6);
                    from = d.setHours(0, 0, 0, 0);
                }

                if (type === "30days") {
                    const d = new Date();
                    d.setDate(d.getDate() - 29);
                    from = d.setHours(0, 0, 0, 0);
                }

                setLocalFilters(prev => ({
                    ...prev,
                    date: {
                        from: new Date(from).toISOString().split("T")[0],
                        to: new Date(to).toISOString().split("T")[0]
                    }
                }));
            };

            return (
                <Box sx={{ px: 2, py: 1 }}>
                    <Stack spacing={2}>



                        {/* Manual Date */}
                        <Stack spacing={1.5}>
                            <TextField
                                type="date"
                                label="From Date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={localFilters.date?.from || ""}
                                onChange={(e) =>
                                    setLocalFilters(prev => ({
                                        ...prev,
                                        date: { ...prev.date, from: e.target.value }
                                    }))
                                }
                            />

                            <TextField
                                type="date"
                                label="To Date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={localFilters.date?.to || ""}
                                onChange={(e) =>
                                    setLocalFilters(prev => ({
                                        ...prev,
                                        date: { ...prev.date, to: e.target.value }
                                    }))
                                }
                            />
                        </Stack>

                    </Stack>
                </Box>
            );
        }
        if (activeType === "direction") {
            return (
                <Box sx={{ overflowY: "auto", maxHeight: 260 }}>
                    {CALL_TYPE_OPTIONS.map(opt => {
                        const checked = (localFilters.direction || []).includes(opt.value);

                        return (
                            <Box key={opt.value}
                                onClick={() => toggle("direction", opt.value)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.25,
                                    px: 1.5,
                                    py: 0.85,
                                    cursor: "pointer",
                                    background: checked ? C.indigoSoft : "transparent",
                                    "&:hover": { background: checked ? C.indigoSoft : C.page }
                                }}
                            >
                                <Checkbox checked={checked} size="small" />
                                <Typography sx={{
                                    fontSize: 12.5,
                                    color: checked ? C.indigo : C.txt1,
                                    fontWeight: checked ? 600 : 400
                                }}>
                                    {opt.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            );
        }
        return null;
    };

    return (
        <Popover open={open} anchorEl={anchorEl} onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ elevation: 0, sx: { mt: 0.75, borderRadius: 3, border: `1px solid ${C.border}`, boxShadow: "0 8px 32px rgba(0,0,0,.12)", overflow: "hidden", width: 420, fontFamily: C.sans } }}>

            {/* Header */}
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: C.page }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <FilterList sx={{ fontSize: 15, color: C.indigo }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.txt1, fontFamily: C.sans }}>Filters</Typography>
                    {/* {totalSelected > 0 && (
                        <Box sx={{ px: 0.8, py: 0.1, borderRadius: 10, background: C.indigo, color: "#fff", fontSize: 11, fontWeight: 700 }}>
                            {totalSelected}
                        </Box>
                    )} */}
                </Stack>
                <IconButton size="small" onClick={onClose} sx={{ color: C.txt4 }}>
                    <Close sx={{ fontSize: 15 }} />
                </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{ display: "flex", minHeight: 280 }}>
                {/* Left nav */}
                <Box sx={{ width: 140, borderRight: `1px solid ${C.border}`, background: C.page, flexShrink: 0, pt: 0.5 }}>
                    {filterTypes.map(ft => {
                        const count = (localFilters[ft.key] || []).length;
                        const isActive = activeType === ft.key;
                        return (
                            <Box key={ft.key} onClick={() => setActiveType(ft.key)}
                                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1, cursor: "pointer", background: isActive ? C.white : "transparent", borderLeft: isActive ? `3px solid ${C.indigo}` : "3px solid transparent", "&:hover": { background: isActive ? C.white : alpha(C.indigo, 0.04) }, transition: "all .12s" }}>
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                    <Box sx={{ color: isActive ? C.indigo : C.txt4 }}>{ft.icon}</Box>
                                    <Typography sx={{ fontSize: 12.5, fontFamily: C.sans, fontWeight: isActive ? 600 : 400, color: isActive ? C.indigo : C.txt2 }}>
                                        {ft.label}
                                    </Typography>
                                </Stack>
                                {count > 0 && (
                                    <Box sx={{ minWidth: 18, height: 18, borderRadius: 10, background: C.indigo, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", px: 0.5 }}>
                                        {count}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {/* Right options */}
                <Box sx={{ flex: 1, pt: 1, pb: 0.5, minWidth: 0 }}>
                    <Typography sx={{ px: 1.5, pb: 0.75, fontSize: 10.5, fontWeight: 700, color: C.txt4, textTransform: "uppercase", letterSpacing: ".06em" }}>
                        {filterTypes.find(f => f.key === activeType)?.label}
                    </Typography>
                    {renderOptions()}
                </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ px: 1.5, py: 1, borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: C.page, gap: 1 }}>
                <Button size="small" onClick={() => setLocalFilters({ sm: [], project: [], status: [], date: { from: null, to: null } })}
                    sx={{ fontSize: 12, color: C.txt3, textTransform: "none", fontFamily: C.sans }}>
                    Clear all
                </Button>
                <Stack direction="row" spacing={0.75}>
                    <Button size="small" onClick={onClose}
                        sx={{ fontSize: 12, color: C.txt3, textTransform: "none", fontFamily: C.sans, border: `1px solid ${C.border}`, borderRadius: 2, px: 1.5 }}>
                        Cancel
                    </Button>
                    <Button size="small" variant="contained" onClick={() => { onApply(localFilters); onClose(); }}
                        sx={{ fontSize: 12, textTransform: "none", fontFamily: C.sans, fontWeight: 600, background: C.indigo, borderRadius: 2, px: 2, "&:hover": { background: C.indigo2 } }}>
                        Apply{totalSelected > 0 ? ` (${totalSelected})` : ""}
                    </Button>
                </Stack>
            </Box>
        </Popover>
    );
}
/* ── Helpers ─────────────────────────────────────────────────────────── */
const fmtDur = s => {
    if (!s) return "0s";
    const m = Math.floor(s / 60), sec = Math.floor(s % 60);
    return m ? `${m}m ${sec}s` : `${sec}s`;
};
const fmtTalkTime = s => {
    if (!s) return "0m";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h ? `${h}h ${m}m` : `${m}m`;
};
const pct = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0;
const deltaPct = (curr, prev) => prev > 0 ? Math.round(((curr - prev) / prev) * 100) : (curr > 0 ? 100 : 0);
const initials = n => { if (!n) return "?"; const p = n.trim().split(" "); return (p.length >= 2 ? p[0][0] + p[1][0] : p[0].slice(0, 2)).toUpperCase(); };
const AV_COLORS = ["#6366f1", "#0ea5e9", "#8b5cf6", "#ec4899", "#f59e0b", "#22c55e", "#ef4444"];
const avColor = n => { if (!n) return C.txt4; let h = 0; for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) & 0xffffffff; return AV_COLORS[Math.abs(h) % AV_COLORS.length]; };

/* ── Styled Surface ──────────────────────────────────────────────────── */
const Surface = styled(Paper)({
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,.05)",
});

/* ── KPI Card ────────────────────────────────────────────────────────── */
function KpiCard({ label, value, sub, color, soft, icon, delta, deltaLabel }) {
    const up = delta > 0, down = delta < 0;
    const deltaColor = up ? C.green : down ? C.red : C.txt4;
    const DeltaIcon = up ? ArrowUpward : down ? ArrowDownward : null;
    return (
        <Surface elevation={0} sx={{
            p: { xs: "12px 14px", sm: "14px 16px" },
            borderTop: `3px solid ${color}`,
            transition: "transform .15s, box-shadow .15s",
            height: "100%",
            "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,0,0,.08)" },
        }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{
                        fontSize: { xs: 20, sm: 24 }, fontWeight: 800, color: C.txt1,
                        fontFamily: C.mono, letterSpacing: "-1px", lineHeight: 1,
                    }}>{value}</Typography>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: C.txt1, mt: 0.6 }}>{label}</Typography>
                    {sub && <Typography sx={{ fontSize: 10.5, color: C.txt4, mt: 0.2 }}>{sub}</Typography>}
                    {DeltaIcon && (
                        <Stack direction="row" spacing={0.3} alignItems="center" sx={{ mt: 0.5 }}>
                            <DeltaIcon sx={{ fontSize: 11, color: deltaColor }} />
                            <Typography sx={{ fontSize: 10.5, color: deltaColor, fontWeight: 700, fontFamily: C.mono }}>
                                {Math.abs(delta)}% vs {deltaLabel}
                            </Typography>
                        </Stack>
                    )}
                    {!DeltaIcon && typeof delta === "number" && deltaLabel && (
                        <Typography sx={{ fontSize: 10.5, color: C.txt4, mt: 0.5, fontFamily: C.mono }}>
                            No change vs {deltaLabel}
                        </Typography>
                    )}
                </Box>
                <Box sx={{
                    width: 32, height: 32, borderRadius: 9, background: soft,
                    display: "flex", alignItems: "center", justifyContent: "center", color,
                    flexShrink: 0,
                }}>{icon}</Box>
            </Stack>
        </Surface>
    );
}

/* ── Section Header ──────────────────────────────────────────────────── */
function SectionHeader({ icon, title, subtitle }) {
    return (
        <Box sx={{ mb: 1.25 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{
                    width: 28, height: 28, borderRadius: 8, background: C.indigoSoft,
                    display: "flex", alignItems: "center", justifyContent: "center", color: C.indigo,
                }}>{icon}</Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.txt1, fontFamily: C.sans }}>{title}</Typography>
            </Stack>
            {subtitle && <Typography sx={{ fontSize: 11.5, color: C.txt4, mt: 0.25, ml: 4.5 }}>{subtitle}</Typography>}
        </Box>
    );
}

/* ── Keyword Card ────────────────────────────────────────────────────── */
function KeywordCard({ k }) {
    return (
        <Box sx={{
            background: k.soft, border: `1px solid ${k.bd}`, borderRadius: 10,
            p: 1.25, display: "flex", alignItems: "center", gap: 1.25, minWidth: 0,
        }}>
            <Box sx={{
                width: 34, height: 34, borderRadius: 10, background: alpha(k.color, 0.15),
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, flexShrink: 0,
            }}>{k.icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 11, color: k.color, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em" }}>
                    {k.label}
                </Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: C.txt1, fontFamily: C.mono, lineHeight: 1, mt: 0.3 }}>
                    {k.count}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: C.txt4, mt: 0.25 }}>
                    {k.count === 1 ? "call" : "calls"} detected
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Alert Row ───────────────────────────────────────────────────────── */
function AlertRow({ severity, title, body, Icon }) {
    const sev = severity === "error"
        ? { color: C.red, soft: C.redSoft, bd: C.redBd }
        : severity === "warning"
            ? { color: C.amber, soft: C.amberSoft, bd: C.amberBd }
            : { color: C.green, soft: C.greenSoft, bd: C.greenBd };
    return (
        <Box sx={{
            display: "flex", alignItems: "flex-start", gap: 1,
            p: 1.25, borderRadius: 10, background: sev.soft, border: `1px solid ${sev.bd}`,
        }}>
            <Box sx={{
                width: 26, height: 26, borderRadius: 8, flexShrink: 0,
                background: alpha(sev.color, 0.15), color: sev.color,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <Icon sx={{ fontSize: 15 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: sev.color }}>{title}</Typography>
                {body && <Typography sx={{ fontSize: 11.5, color: C.txt2, lineHeight: 1.5, mt: 0.15 }}>{body}</Typography>}
            </Box>
        </Box>
    );
}

/* ── Multi-Select Filter Popover ─────────────────────────────────────── */
function MultiFilter({ label, icon, options, getValue, getLabel, selected, onChange }) {
    const [anchor, setAnchor] = useState(null);
    const [q, setQ] = useState("");
    const filtered = useMemo(
        () => options.filter(o => getLabel(o).toLowerCase().includes(q.toLowerCase())),
        [options, q, getLabel]
    );
    const toggle = val => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);

    return (
        <>
            <Button
                size="small" startIcon={icon} onClick={e => setAnchor(e.currentTarget)}
                sx={{
                    textTransform: "none", fontFamily: `${C.sans} !important`, fontWeight: 600, fontSize: 12,
                    background: selected.length ? C.indigoSoft : C.white, color: selected.length ? C.indigo : C.txt2,
                    border: `1px solid ${selected.length ? C.indigo : C.border}`,
                    px: 1.5, py: 0.5, borderRadius: 2, whiteSpace: "nowrap", minWidth: 0,
                    "&:hover": { background: C.indigoSoft, borderColor: C.indigo },
                }}
            >
                {label}{selected.length ? ` (${selected.length})` : ""}
            </Button>
            <Popover
                open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                PaperProps={{
                    sx: {
                        mt: 0.5, borderRadius: 2, minWidth: 240, maxWidth: 320,
                        border: `1px solid ${C.border}`, boxShadow: "0 8px 24px rgba(0,0,0,.1)",
                    },
                }}
            >
                <Box sx={{ p: 1 }}>
                    <TextField
                        size="small" fullWidth placeholder={`Search ${label.toLowerCase()}...`}
                        value={q} onChange={e => setQ(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 14, color: C.txt4 }} /></InputAdornment>,
                            sx: { fontSize: 12.5, fontFamily: C.sans, height: 32 },
                        }}
                    />
                </Box>
                <Box sx={{ maxHeight: 280, overflowY: "auto", pb: 0.5 }}>
                    {filtered.length === 0 ? (
                        <Typography sx={{ px: 2, py: 2, fontSize: 12, color: C.txt4, textAlign: "center" }}>No results</Typography>
                    ) : filtered.map(o => {
                        const val = getValue(o);
                        const checked = selected.includes(val);
                        return (
                            <Box key={val} onClick={() => toggle(val)}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.6,
                                    cursor: "pointer",
                                    background: checked ? C.indigoSoft : "transparent",
                                    "&:hover": { background: checked ? C.indigoSoft : C.page },
                                }}>
                                <Checkbox size="small" checked={checked} sx={{ p: 0, color: C.border, "&.Mui-checked": { color: C.indigo } }} />
                                <Typography sx={{ fontSize: 12.5, color: checked ? C.indigo : C.txt1, fontWeight: checked ? 600 : 400 }}>
                                    {getLabel(o)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", p: 0.75, borderTop: `1px solid ${C.border}` }}>
                    <Button size="small" onClick={() => onChange([])} sx={{ fontSize: 11, color: C.txt3, textTransform: "none" }}>Clear</Button>
                    <Button size="small" onClick={() => setAnchor(null)} sx={{ fontSize: 11, color: C.indigo, textTransform: "none", fontWeight: 700 }}>Done</Button>
                </Box>
            </Popover>
        </>
    );
}

const FILTER_TYPES = [
    { key: "sm", label: "SM", icon: <BusinessCenter sx={{ fontSize: 14 }} /> },
    { key: "project", label: "Project", icon: <FolderOpen sx={{ fontSize: 14 }} /> },
    { key: "stage", label: "Lead Stage", icon: <Insights sx={{ fontSize: 14 }} /> },
    { key: "direction", label: "Call Type", icon: <PhoneEnabled sx={{ fontSize: 14 }} /> },
    { key: "date", label: "Date", icon: <CalendarToday sx={{ fontSize: 14 }} /> },
];
const CALL_STATUS_OPTIONS = [
    { value: "answered", label: "Answered", color: C.green },
    { value: "missed", label: "Missed", color: C.red },
    { value: "not answered", label: "Not Answered", color: C.amber },
];

const CALL_TYPE_OPTIONS = [
    { value: "incoming", label: "Incoming" },
    { value: "outbound", label: "Outbound" },
    { value: "clicktocall", label: "Click-to-Call" },
];

const LEAD_STAGE_OPTIONS = Object.entries(LEAD_STATUS_MAP).map(([value, { label }]) => ({
    value,
    label
}));
const LEAD_STATUS_OPTIONS = Object.entries(LEAD_STATUS_MAP).map(([value, { label, color }]) => ({ value, label, color }));
/* ── Main Dashboard ──────────────────────────────────────────────────── */
export default function CallLogsDashboard({ visibleSmIds, isSuperRole, isSalesManager, smOptions, projectOptions, callLogsShowMode }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const getDefaultRange = () => {
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return {
            from: now.toISOString().split("T")[0], // "YYYY-MM-DD"
            to: now.toISOString().split("T")[0],
            preset: "Today",
        };
    };
    const [dateRange, setDateRange] = useState(getDefaultRange());
    const [filters, setFilters] = useState({ sm: [], project: [], stage: [], direction: [] });

    const hierarchyLoading = visibleSmIds === null && callLogsShowMode !== "All";
    const [filterAnchor, setFilterAnchor] = useState(null);
    const fetchData = useCallback(async () => {
        if (hierarchyLoading) return;
        setLoading(true);
        try {
            const fromTs = new Date(`${dateRange.from}T00:00:00`).getTime();
            const toTs = new Date(`${dateRange.to}T23:59:59`).getTime();
            let q = supabase.from("CallLogs").select("*")
                .gte("startedAt", fromTs)
                .lte("startedAt", toTs)
                .order("startedAt", { ascending: false })
            // .limit(2000);

            // REPLACE the entire visibility block in fetchData:

            if (callLogsShowMode === "All") {
                // No filter — role config says see everything

            } else if (isSuperRole) {
                // Root of org — no filter, sees everything

            } else {
                // Mid-level manager or leaf agent — scope to their visible leaf IDs
                if (!visibleSmIds || visibleSmIds.length === 0) {
                    setRows([]);
                    setLoading(false);
                    return;
                }
                q = q.in("userId", visibleSmIds);
            }

            if (filters.sm.length > 0) q = q.in("userId", filters.sm);
            if (filters.direction.length > 0) q = q.in("direction", filters.direction);

            if (filters.project.length > 0) {
                const { data: leads } = await supabase.from("Leads").select("leadId").in("projectId", filters.project);
                const ids = (leads || []).map(l => l.leadId);
                if (ids.length === 0) { setRows([]); setLoading(false); return; }
                q = q.in("leadId", ids);
            }

            const { data, error } = await q;
            if (error) throw error;

            let filtered = data || [];
            if (filters.stage.length > 0) {
                const stageLabels = filters.stage.map(s => LEAD_STATUS_MAP[s]?.label).filter(Boolean);
                filtered = filtered.filter(r => stageLabels.includes(r.leadStatusSnapshot));
            }
            setRows(filtered);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [hierarchyLoading, isSuperRole, visibleSmIds, dateRange, filters, callLogsShowMode]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const stats = useMemo(() => {
        const todayStart = new Date().setHours(0, 0, 0, 0);
        const yesterdayStart = todayStart - 86400000;
        const weekAgo = todayStart - 7 * 86400000;
        const twoWeeksAgo = todayStart - 14 * 86400000;

        const answered = rows.filter(r => r.callStatus === "answered");
        const missed = rows.filter(r => r.callStatus === "missed");
        const notAnswered = rows.filter(r => r.callStatus === "not answered");
        const rejected = rows.filter(r => r.callStatus === "rejected");
        const answeredDur = answered.reduce((s, r) => s + (r.durationSec || 0), 0);
        const uniqueLeads = new Set(rows.map(r => r.leadId).filter(Boolean)).size;

        const inRange = (r, s, e) => r.startedAt >= s && (e == null || r.startedAt < e);
        const todayCalls = rows.filter(r => inRange(r, todayStart)).length;
        const yesterdayCalls = rows.filter(r => inRange(r, yesterdayStart, todayStart)).length;
        const thisWeekCalls = rows.filter(r => inRange(r, weekAgo)).length;
        const lastWeekCalls = rows.filter(r => inRange(r, twoWeeksAgo, weekAgo)).length;

        const thisWeekAnswered = rows.filter(r => inRange(r, weekAgo) && r.callStatus === "answered");
        const lastWeekAnswered = rows.filter(r => inRange(r, twoWeeksAgo, weekAgo) && r.callStatus === "answered");
        const thisWeekConnPct = pct(thisWeekAnswered.length, thisWeekCalls);
        const lastWeekConnPct = pct(lastWeekAnswered.length, lastWeekCalls);
        const thisWeekMissedPct = pct(thisWeekCalls - thisWeekAnswered.length, thisWeekCalls);
        const lastWeekMissedPct = pct(lastWeekCalls - lastWeekAnswered.length, lastWeekCalls);
        const thisWeekAvgDur = thisWeekAnswered.length ? Math.round(thisWeekAnswered.reduce((s, r) => s + (r.durationSec || 0), 0) / thisWeekAnswered.length) : 0;
        const lastWeekAvgDur = lastWeekAnswered.length ? Math.round(lastWeekAnswered.reduce((s, r) => s + (r.durationSec || 0), 0) / lastWeekAnswered.length) : 0;
        const thisWeekTalk = thisWeekAnswered.reduce((s, r) => s + (r.durationSec || 0), 0);
        const lastWeekTalk = lastWeekAnswered.reduce((s, r) => s + (r.durationSec || 0), 0);

        const shortC = answered.filter(r => r.durationSec < 30).length;
        const medC = answered.filter(r => r.durationSec >= 30 && r.durationSec < 120).length;
        const longC = answered.filter(r => r.durationSec >= 120).length;

        const incomingC = rows.filter(r => r.direction === "incoming" || r.direction === "inbound").length;
        const outgoingC = rows.length - incomingC;

        const byStage = {};
        rows.forEach(r => {
            const stage = r.leadStatusSnapshot || "Unknown";
            if (!byStage[stage]) byStage[stage] = { total: 0, answered: 0, durSum: 0 };
            byStage[stage].total++;
            if (r.callStatus === "answered") { byStage[stage].answered++; byStage[stage].durSum += r.durationSec || 0; }
        });
        const stagesList = Object.entries(byStage).map(([stage, s]) => ({
            stage, total: s.total, answered: s.answered,
            avgDur: s.answered > 0 ? Math.round(s.durSum / s.answered) : 0,
            connPct: pct(s.answered, s.total),
        })).sort((a, b) => b.total - a.total);

        const byAgent = {};
        rows.forEach(r => {
            const key = r.userId || r.agentName || "unknown";
            if (!byAgent[key]) byAgent[key] = { name: r.agentName, total: 0, answered: 0, durSum: 0, leads: new Set() };
            byAgent[key].total++;
            if (r.callStatus === "answered") { byAgent[key].answered++; byAgent[key].durSum += r.durationSec || 0; }
            if (r.leadId) byAgent[key].leads.add(r.leadId);
        });
        const agentsList = Object.entries(byAgent).map(([id, a]) => ({
            id, name: a.name || "Unknown",
            total: a.total, connPct: pct(a.answered, a.total),
            avgDur: a.answered > 0 ? Math.round(a.durSum / a.answered) : 0,
            leads: a.leads.size,
        })).sort((a, b) => b.total - a.total);

        const qualified = agentsList.filter(a => a.total >= 5);
        const byConn = [...qualified].sort((a, b) => b.connPct - a.connPct);
        const topCount = Math.min(2, Math.ceil(byConn.length * 0.2));
        const lowCount = Math.min(2, Math.ceil(byConn.length * 0.2));
        const topIds = new Set(byConn.slice(0, topCount).map(a => a.id));
        const lowIds = new Set(byConn.slice(-lowCount).filter(a => a.connPct < 50).map(a => a.id));

        const keywords = KEYWORDS.map(kw => {
            const count = rows.filter(r => {
                const text = `${r.aiShortSummary || ""} ${r.aiDetailedSummary || ""} ${r.transcription || ""}`;
                return kw.patterns.some(p => p.test(text));
            }).length;
            return { ...kw, count };
        });

        const positive = rows.filter(r => r.aiLeadTemperature === "hot").length;
        const neutral = rows.filter(r => r.aiLeadTemperature === "warm").length;
        const negative = rows.filter(r => r.aiLeadTemperature === "cold").length;

        const alerts = [];
        const missedRate = pct(missed.length, rows.length);
        const connRate = pct(answered.length, rows.length);
        if (rows.length >= 10 && missedRate >= 30) {
            alerts.push({
                severity: "error", Icon: PhoneMissed,
                title: `High missed call rate: ${missedRate}%`,
                body: `${missed.length} of ${rows.length} calls in the last ${dateRange} days were missed. Consider adjusting pickup coverage or callback cadence.`,
            });
        }
        if (rows.length >= 10 && connRate < 60) {
            alerts.push({
                severity: "warning", Icon: TrendingDown,
                title: `Low connection rate: ${connRate}%`,
                body: `Only ${answered.length} of ${rows.length} calls connected. Review call-out timings and agent availability.`,
            });
        }
        const hotLeadIds = new Set(rows.filter(r => r.aiLeadTemperature === "hot" && r.leadId).map(r => r.leadId));
        const followedUp = new Set(rows.filter(r => r.callStatus === "answered" && r.leadId && hotLeadIds.has(r.leadId)).map(r => r.leadId));
        const unFollowedHot = [...hotLeadIds].filter(id => !followedUp.has(id)).length;
        if (unFollowedHot > 0) {
            alerts.push({
                severity: "warning", Icon: LocalFireDepartment,
                title: `${unFollowedHot} hot ${unFollowedHot === 1 ? "lead" : "leads"} without follow-up`,
                body: `AI flagged ${unFollowedHot} high-intent ${unFollowedHot === 1 ? "lead" : "leads"} that have no answered follow-up call yet.`,
            });
        }
        if (alerts.length === 0 && rows.length > 0) {
            alerts.push({
                severity: "info", Icon: CheckCircle,
                title: "All clear — no flagged issues",
                body: "Call quality metrics look healthy for the current period.",
            });
        }

        return {
            total: rows.length,
            answered: answered.length, missed: missed.length, notAnswered: notAnswered.length, rejected: rejected.length,
            connRate, missedRate,
            avgDur: answered.length ? Math.round(answeredDur / answered.length) : 0,
            totalTalk: answeredDur,
            uniqueLeads,
            callsPerLead: uniqueLeads > 0 ? (rows.length / uniqueLeads).toFixed(1) : "0",
            todayCalls, yesterdayCalls,
            thisWeekCalls, lastWeekCalls,
            thisWeekConnPct, lastWeekConnPct,
            thisWeekMissedPct, lastWeekMissedPct,
            thisWeekAvgDur, lastWeekAvgDur,
            thisWeekTalk, lastWeekTalk,
            shortC, medC, longC,
            incomingC, outgoingC,
            stagesList,
            agentsList, topIds, lowIds,
            keywords,
            positive, neutral, negative,
            alerts,
        };
    }, [rows, dateRange]);

    const statusChart = {
        labels: ["Answered", "Missed", "Not Answered", "Rejected"],
        datasets: [{
            data: [stats.answered, stats.missed, stats.notAnswered, stats.rejected],
            backgroundColor: [C.green, C.red, C.amber, C.sky],
            borderColor: C.white, borderWidth: 2,
        }],
    };
    const durationChart = {
        labels: ["Short (<30s)", "Medium (30s–2m)", "Long (>2m)"],
        datasets: [{
            data: [stats.shortC, stats.medC, stats.longC],
            backgroundColor: [C.amber, C.sky, C.green],
            borderRadius: 6,
        }],
    };
    const directionChart = {
        labels: ["Incoming", "Outgoing"],
        datasets: [{
            data: [stats.incomingC, stats.outgoingC],
            backgroundColor: [C.sky, C.indigo],
            borderRadius: 6,
        }],
    };
    const sentimentChart = {
        labels: ["Positive (Hot)", "Neutral (Warm)", "Negative (Cold)"],
        datasets: [{
            data: [stats.positive, stats.neutral, stats.negative],
            backgroundColor: [C.green, C.amber, C.sky],
            borderColor: C.white, borderWidth: 2,
        }],
    };

    const hasData = rows.length > 0;
    const sentimentTotal = stats.positive + stats.neutral + stats.negative;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, animation: "fadeUp .2s ease both" }}>
            {/* Filter Bar */}
            <Surface elevation={0} sx={{ px: { xs: 1.25, sm: 1.5 }, py: { xs: 1.25, sm: 1.5 } }}>
                <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between" gap={1.25}>
                    {/* Row 1 — Period presets + custom dates */}
                    <Box sx={{ pt: 0.25 }}>


                        {/* Preset chips */}
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
                            {[
                                {
                                    label: "Today",
                                    getRange: () => {
                                        const now = new Date();
                                        return {
                                            from: now.toISOString().split("T")[0],
                                            to: now.toISOString().split("T")[0],
                                            preset: "Today",
                                        };
                                    },
                                },

                                {
                                    label: "Last 7d",
                                    getRange: () => {
                                        const now = new Date();
                                        const from = new Date(now); from.setDate(from.getDate() - 6);
                                        return { from: from.toISOString().split("T")[0], to: now.toISOString().split("T")[0], preset: "Last 7d" };
                                    },
                                },
                                {
                                    label: "Last 30d",
                                    getRange: () => {
                                        const now = new Date();
                                        const from = new Date(now); from.setDate(from.getDate() - 29);
                                        return { from: from.toISOString().split("T")[0], to: now.toISOString().split("T")[0], preset: "Last 30d" };
                                    },
                                },
                                {
                                    label: "MTD",
                                    getRange: () => {
                                        const now = new Date();
                                        return {
                                            from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0],
                                            to: now.toISOString().split("T")[0],
                                            preset: "MTD",
                                        };
                                    },
                                },

                                {
                                    label: "This FY",
                                    getRange: () => {
                                        const now = new Date();
                                        // FY starts April 1st — if current month is Jan/Feb/Mar, FY started last year
                                        const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
                                        return {
                                            from: new Date(fyStartYear, 3, 1).toISOString().split("T")[0], // month 3 = April
                                            to: now.toISOString().split("T")[0],
                                            preset: "This FY",
                                        };
                                    },
                                },
                            ].map(p => {
                                const isActive = dateRange.preset === p.label;
                                return (
                                    <Chip
                                        key={p.label}
                                        label={p.label}
                                        size="small"
                                        clickable
                                        onClick={() => setDateRange(p.getRange())}
                                        sx={{
                                            fontFamily: C.sans, fontWeight: 700, fontSize: 11.5,
                                            height: 26, borderRadius: 2, mb: 0.5,
                                            background: isActive ? C.indigo : C.white,
                                            color: isActive ? "#fff" : C.txt2,
                                            border: `1px solid ${isActive ? C.indigo : C.border}`,
                                            "&:hover": { background: isActive ? C.indigo2 : C.indigoSoft, borderColor: C.indigo },
                                            transition: "all .15s",
                                        }}
                                    />
                                );
                            })}
                        </Stack>

                        {/* Custom date pickers */}

                    </Box>

                    {/* Row 2 — Dimension filters + Refresh */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            flexShrink: 0
                        }}
                    >

                        {/* 🔥 Filter Icon */}
                        <IconButton
                            size="large"
                            onClick={(e) => setFilterAnchor(e.currentTarget)}
                            sx={{
                                border: `1px solid ${C.border}`,
                                borderRadius: 2,
                                width: 30,
                                height: 30,
                                background: C.white,
                                "&:hover": {
                                    background: C.indigoSoft,
                                    borderColor: C.indigo
                                }
                            }}
                        >
                            <FilterList sx={{ fontSize: 16 }} />
                        </IconButton>

                        {/* 🔥 Refresh Icon */}
                        <IconButton
                            size="medium"
                            onClick={fetchData}
                            disabled={loading}
                            sx={{
                                border: `1px solid ${C.border}`,
                                borderRadius: 2,
                                width: 30,
                                height: 30,
                                background: C.white,
                                "&:hover": {
                                    background: C.indigoSoft
                                }
                            }}
                        >
                            <Refresh sx={{ fontSize: 15 }} />
                        </IconButton>

                    </Box>
                </Box>

            </Surface>

            {loading && (
                <LinearProgress sx={{
                    height: 2, background: C.indigoSoft,
                    "& .MuiLinearProgress-bar": { background: `linear-gradient(90deg,${C.indigo},${C.indigo2})` },
                }} />
            )}

            {!hierarchyLoading && !loading && !hasData && (
                <Surface elevation={0} sx={{ p: 6, textAlign: "center" }}>
                    <Psychology sx={{ fontSize: 42, color: C.border, mb: 1 }} />
                    <Typography sx={{ color: C.txt3, fontSize: 14, fontWeight: 600 }}>No calls in this period</Typography>
                    <Typography sx={{ color: C.txt4, fontSize: 12, mt: 0.5 }}>Try expanding the date range or clearing filters.</Typography>
                </Surface>
            )}

            {hasData && (
                <>
                    {/* A. KPI SUMMARY */}
                    <Box>
                        <SectionHeader icon={<TrendingUp sx={{ fontSize: 15 }} />} title="KPI Summary" subtitle={`${dateRange.from} → ${dateRange.to}`} />
                        <Grid container spacing={{ xs: 0.75, sm: 1.25 }}>
                            <Grid item xs={6} sm={4} md={12 / 6}>
                                <KpiCard label="Total Calls" value={stats.total}
                                    sub={`${stats.todayCalls} today · ${stats.yesterdayCalls} yesterday`}
                                    color={C.indigo} soft={C.indigoSoft} icon={<PhoneEnabled sx={{ fontSize: 16 }} />}
                                    delta={deltaPct(stats.thisWeekCalls, stats.lastWeekCalls)} deltaLabel="last week" />
                            </Grid>
                            <Grid item xs={6} sm={4} md={12 / 6}>
                                <KpiCard label="Connected %" value={`${stats.connRate}%`}
                                    sub={`${stats.answered} answered`}
                                    color={C.green} soft={C.greenSoft} icon={<CheckCircle sx={{ fontSize: 16 }} />}
                                    delta={stats.thisWeekConnPct - stats.lastWeekConnPct} deltaLabel="last week" />
                            </Grid>
                            <Grid item xs={6} sm={4} md={12 / 6}>
                                <KpiCard label="Missed %" value={`${stats.missedRate}%`}
                                    sub={`${stats.missed} missed`}
                                    color={C.red} soft={C.redSoft} icon={<PhoneMissed sx={{ fontSize: 16 }} />}
                                    delta={stats.thisWeekMissedPct - stats.lastWeekMissedPct} deltaLabel="last week" />
                            </Grid>
                            <Grid item xs={6} sm={4} md={12 / 6}>
                                <KpiCard label="Not Answered" value={stats.notAnswered}
                                    sub="by customer"
                                    color={C.amber} soft={C.amberSoft} icon={<PhoneMissed sx={{ fontSize: 16 }} />} />
                            </Grid>
                            <Grid item xs={6} sm={4} md={12 / 6}>
                                <KpiCard label="Avg Duration" value={fmtDur(stats.avgDur)} sub="per answered call"
                                    color={C.amber} soft={C.amberSoft} icon={<AccessTime sx={{ fontSize: 16 }} />}
                                    delta={deltaPct(stats.thisWeekAvgDur, stats.lastWeekAvgDur)} deltaLabel="last week" />
                            </Grid>
                            <Grid item xs={6} sm={4} md={12 / 6}>
                                <KpiCard label="Total Talk Time" value={fmtTalkTime(stats.totalTalk)} sub="connected minutes"
                                    color={C.sky} soft={C.skySoft} icon={<PhoneInTalk sx={{ fontSize: 16 }} />}
                                    delta={deltaPct(stats.thisWeekTalk, stats.lastWeekTalk)} deltaLabel="last week" />
                            </Grid>
                            {/* <Grid item xs={6} sm={4} md={12 / 7}>
                                <KpiCard label="Calls per Lead" value={stats.callsPerLead} sub={`${stats.uniqueLeads} unique leads`}
                                    color={C.indigo2} soft={C.indigoSoft} icon={<Person sx={{ fontSize: 16 }} />} />
                            </Grid> */} 
                        </Grid>
                    </Box>

                    {/* B. CALL PERFORMANCE */}
                    <Box sx={{ pt:3}}>
                        <SectionHeader icon={<Insights sx={{ fontSize: 15 }} />} title="Call Performance" subtitle="Status, duration, and direction breakdown" />
                        <Grid container spacing={1.25}>
                            <Grid item xs={12} md={4}>
                                <Surface elevation={0} sx={{ p: 1.5, height: "100%" }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.txt1, mb: 1 }}>Call Status Distribution</Typography>
                                    <Box sx={{ height: 200, display: "flex", justifyContent: "center" }}>
                                        <Doughnut data={statusChart} options={{
                                            maintainAspectRatio: false,
                                            plugins: { legend: { position: "bottom", labels: { font: { size: 11, family: "Geist" }, boxWidth: 10, padding: 10 } } },
                                            cutout: "60%",
                                        }} />
                                    </Box>
                                    <Typography sx={{ fontSize: 11, color: C.txt4, mt: 1, lineHeight: 1.5 }}>
                                        <strong style={{ color: C.txt2 }}>Insight: </strong>
                                        {stats.connRate >= 70 ? "Healthy connection rate." : stats.connRate >= 50 ? "Moderate — review pickup timing." : "Low — agent availability may be an issue."}
                                    </Typography>
                                </Surface>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Surface elevation={0} sx={{ p: 1.5, height: "100%" }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.txt1, mb: 1 }}>Duration Buckets</Typography>
                                    <Box sx={{ height: 200 }}>
                                        <Bar data={durationChart} options={{
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                x: { ticks: { font: { size: 10, family: "Geist" }, color: C.txt3 }, grid: { display: false } },
                                                y: { beginAtZero: true, ticks: { font: { size: 10, family: "Geist Mono" }, color: C.txt4, precision: 0 }, grid: { color: C.border2 } },
                                            },
                                        }} />
                                    </Box>
                                    <Typography sx={{ fontSize: 11, color: C.txt4, mt: 1, lineHeight: 1.5 }}>
                                        <strong style={{ color: C.txt2 }}>Insight: </strong>
                                        {stats.shortC > (stats.longC + stats.medC) ? "Many short calls — leads may be getting rushed." : stats.longC > 0 ? "Healthy mix of deep conversations." : "Most calls are brief."}
                                    </Typography>
                                </Surface>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Surface elevation={0} sx={{ p: 1.5, height: "100%" }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.txt1, mb: 1 }}>Incoming vs Outgoing</Typography>
                                    <Box sx={{ height: 200 }}>
                                        <Bar data={directionChart} options={{
                                            maintainAspectRatio: false, indexAxis: "y",
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                x: { beginAtZero: true, ticks: { font: { size: 10, family: "Geist Mono" }, color: C.txt4, precision: 0 }, grid: { color: C.border2 } },
                                                y: { ticks: { font: { size: 11, family: "Geist" }, color: C.txt2 }, grid: { display: false } },
                                            },
                                        }} />
                                    </Box>
                                    <Typography sx={{ fontSize: 11, color: C.txt4, mt: 1, lineHeight: 1.5 }}>
                                        <strong style={{ color: C.txt2 }}>Insight: </strong>
                                        {stats.outgoingC > stats.incomingC ? "Team is actively prospecting." : "Most calls are inbound — ensure proactive follow-ups."}
                                    </Typography>
                                </Surface>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* C. LEAD STAGE MATRIX */}
                    <Box sx={{pt:3}}>
                        <SectionHeader icon={<Insights sx={{ fontSize: 15 }} />} title="Lead Stage × Call Matrix" subtitle="Where calls are happening across the funnel" />
                        <Surface elevation={0} sx={{ overflow: "hidden" }}>
                            <Box sx={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1.2fr", px: 1.5, py: 0.9, background: C.page, borderBottom: `1px solid ${C.border}` }}>
                                {["Lead Stage", "Calls", "Answered", "Avg Duration", "Connection"].map(h => (
                                    <Typography key={h} sx={{ fontSize: 10.5, fontWeight: 700, color: C.txt4, textTransform: "uppercase", letterSpacing: ".06em" }}>{h}</Typography>
                                ))}
                            </Box>
                            {stats.stagesList.length === 0 ? (
                                <Typography sx={{ p: 3, textAlign: "center", fontSize: 12, color: C.txt4 }}>No stage data</Typography>
                            ) : stats.stagesList.map(s => (
                                <Box key={s.stage} sx={{
                                    display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1.2fr",
                                    px: 1.5, py: 1, alignItems: "center",
                                    borderBottom: `1px solid ${C.border2}`,
                                    "&:hover": { background: C.page },
                                }}>
                                    <Typography sx={{ fontSize: 12.5, color: C.txt1, fontWeight: 600 }}>{s.stage}</Typography>
                                    <Typography sx={{ fontSize: 12.5, color: C.txt2, fontFamily: C.mono }}>{s.total}</Typography>
                                    <Typography sx={{ fontSize: 12.5, color: C.txt2, fontFamily: C.mono }}>{s.answered}</Typography>
                                    <Typography sx={{ fontSize: 12.5, color: C.txt2, fontFamily: C.mono }}>{fmtDur(s.avgDur)}</Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Box sx={{ flex: 1, height: 5, background: C.border2, borderRadius: 3, overflow: "hidden", maxWidth: 80 }}>
                                            <Box sx={{ height: "100%", width: `${s.connPct}%`, background: s.connPct >= 70 ? C.green : s.connPct >= 40 ? C.amber : C.red }} />
                                        </Box>
                                        <Typography sx={{ fontSize: 11.5, fontFamily: C.mono, fontWeight: 700, color: s.connPct >= 70 ? C.green : s.connPct >= 40 ? C.amber : C.red }}>
                                            {s.connPct}%
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Surface>
                    </Box>

                    {/* D. AGENT PERFORMANCE */}
                    <Box>
                        <SectionHeader icon={<EmojiEvents sx={{ fontSize: 15 }} />} title="Agent Performance" subtitle="Top & low performers highlighted (≥5 calls)" />
                        <Surface elevation={0} sx={{ overflow: "hidden" }}>
                            <Box sx={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 1.2fr 1fr 0.8fr 0.8fr", px: 1.5, py: 0.9, background: C.page, borderBottom: `1px solid ${C.border}` }}>
                                {["Sales Manager", "Total", "Connected %", "Avg Duration", "Leads", ""].map((h, i) => (
                                    <Typography key={i} sx={{ fontSize: 10.5, fontWeight: 700, color: C.txt4, textTransform: "uppercase", letterSpacing: ".06em" }}>{h}</Typography>
                                ))}
                            </Box>
                            {stats.agentsList.length === 0 ? (
                                <Typography sx={{ p: 3, textAlign: "center", fontSize: 12, color: C.txt4 }}>No agent data</Typography>
                            ) : stats.agentsList.map(a => {
                                const isTop = stats.topIds.has(a.id);
                                const isLow = stats.lowIds.has(a.id);
                                return (
                                    <Box key={a.id} sx={{
                                        display: "grid", gridTemplateColumns: "2fr 0.8fr 1.2fr 1fr 0.8fr 0.8fr",
                                        px: 1.5, py: 1, alignItems: "center",
                                        borderBottom: `1px solid ${C.border2}`,
                                        background: isTop ? alpha(C.green, 0.04) : isLow ? alpha(C.red, 0.04) : "transparent",
                                        "&:hover": { background: isTop ? alpha(C.green, 0.07) : isLow ? alpha(C.red, 0.07) : C.page },
                                    }}>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                            <Avatar sx={{ width: 26, height: 26, fontSize: 10, fontWeight: 700, background: avColor(a.name) }}>{initials(a.name)}</Avatar>
                                            <Typography sx={{ fontSize: 12.5, color: C.txt1, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {a.name}
                                            </Typography>
                                        </Stack>
                                        <Typography sx={{ fontSize: 12.5, color: C.txt2, fontFamily: C.mono }}>{a.total}</Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                            <Box sx={{ width: 50, height: 5, background: C.border2, borderRadius: 3, overflow: "hidden" }}>
                                                <Box sx={{ height: "100%", width: `${a.connPct}%`, background: a.connPct >= 70 ? C.green : a.connPct >= 40 ? C.amber : C.red }} />
                                            </Box>
                                            <Typography sx={{ fontSize: 11.5, fontFamily: C.mono, fontWeight: 700, color: a.connPct >= 70 ? C.green : a.connPct >= 40 ? C.amber : C.red }}>
                                                {a.connPct}%
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 12.5, color: C.txt2, fontFamily: C.mono }}>{fmtDur(a.avgDur)}</Typography>
                                        <Typography sx={{ fontSize: 12.5, color: C.txt2, fontFamily: C.mono }}>{a.leads}</Typography>
                                        <Box>
                                            {isTop && (
                                                <Chip size="small" icon={<Star sx={{ fontSize: "12px !important" }} />} label="Top" sx={{
                                                    fontSize: 10, fontWeight: 700, height: 20, borderRadius: 2,
                                                    background: C.greenSoft, color: C.green, border: `1px solid ${C.greenBd}`,
                                                    "& .MuiChip-icon": { color: C.green },
                                                }} />
                                            )}
                                            {isLow && (
                                                <Chip size="small" icon={<TrendingDown sx={{ fontSize: "12px !important" }} />} label="Low" sx={{
                                                    fontSize: 10, fontWeight: 700, height: 20, borderRadius: 2,
                                                    background: C.redSoft, color: C.red, border: `1px solid ${C.redBd}`,
                                                    "& .MuiChip-icon": { color: C.red },
                                                }} />
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Surface>
                    </Box>

                    {/* AI INTELLIGENCE */}
                    <Box>
                        <SectionHeader icon={<Psychology sx={{ fontSize: 15 }} />} title="AI Intelligence" subtitle="Keywords, sentiment, and actionable alerts" />
                        <Grid container spacing={1.25}>
                            <Grid item xs={12} md={8}>
                                <Surface elevation={0} sx={{ p: 1.5, mb: 1.25 }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.indigo, textTransform: "uppercase", letterSpacing: ".07em", mb: 1 }}>
                                        Detected Keywords
                                    </Typography>
                                    <Grid container spacing={1}>
                                        {stats.keywords.map(k => (
                                            <Grid item xs={6} sm={3} key={k.key}>
                                                <KeywordCard k={k} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Surface>
                                <Surface elevation={0} sx={{ p: 1.5 }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.red, textTransform: "uppercase", letterSpacing: ".07em", mb: 1 }}>
                                        Alerts ({stats.alerts.length})
                                    </Typography>
                                    <Stack spacing={0.75}>
                                        {stats.alerts.map((a, i) => <AlertRow key={i} {...a} />)}
                                    </Stack>
                                </Surface>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Surface elevation={0} sx={{ p: 1.5, height: "100%" }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.indigo, textTransform: "uppercase", letterSpacing: ".07em", mb: 1 }}>
                                        Sentiment
                                    </Typography>
                                    <Box sx={{ height: 200, display: "flex", justifyContent: "center" }}>
                                        {sentimentTotal === 0 ? (
                                            <Typography sx={{ alignSelf: "center", fontSize: 12, color: C.txt4, textAlign: "center" }}>No AI-analyzed calls yet</Typography>
                                        ) : (
                                            <Doughnut data={sentimentChart} options={{
                                                maintainAspectRatio: false,
                                                plugins: { legend: { position: "bottom", labels: { font: { size: 10, family: "Geist" }, boxWidth: 10, padding: 8 } } },
                                                cutout: "55%",
                                            }} />
                                        )}
                                    </Box>
                                    <Stack spacing={0.5} sx={{ mt: 1.25 }}>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <SentimentSatisfied sx={{ fontSize: 14, color: C.green }} />
                                                <Typography sx={{ fontSize: 11.5, color: C.txt2 }}>Positive</Typography>
                                            </Stack>
                                            <Typography sx={{ fontSize: 11.5, color: C.green, fontFamily: C.mono, fontWeight: 700 }}>{stats.positive}</Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <SentimentNeutral sx={{ fontSize: 14, color: C.amber }} />
                                                <Typography sx={{ fontSize: 11.5, color: C.txt2 }}>Neutral</Typography>
                                            </Stack>
                                            <Typography sx={{ fontSize: 11.5, color: C.amber, fontFamily: C.mono, fontWeight: 700 }}>{stats.neutral}</Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <SentimentDissatisfied sx={{ fontSize: 14, color: C.sky }} />
                                                <Typography sx={{ fontSize: 11.5, color: C.txt2 }}>Negative</Typography>
                                            </Stack>
                                            <Typography sx={{ fontSize: 11.5, color: C.sky, fontFamily: C.mono, fontWeight: 700 }}>{stats.negative}</Typography>
                                        </Stack>
                                    </Stack>
                                </Surface>
                            </Grid>
                        </Grid>
                    </Box>
                </>
            )}
            <FilterPanel
                anchorEl={filterAnchor}
                onClose={() => setFilterAnchor(null)}
                tab={0} // or your tab logic
                filters={{
                    ...filters,
                    date: {
                        from: dateRange.from,
                        to: dateRange.to
                    }
                }}
                onApply={(f) => {
                    setFilters({
                        sm: f.sm || [],
                        project: f.project || [],
                        stage: f.status || [],
                        direction: f.direction || []
                    });

                    if (f.date) {
                        setDateRange(prev => ({
                            ...prev,
                            from: f.date.from,
                            to: f.date.to,
                            preset: "custom"
                        }));
                    }
                }}
                smOptions={smOptions}
                projectOptions={projectOptions}
                isLeafUser={isSalesManager}
            />

        </Box>
    );
}